import React, {Fragment} from 'react';
import {OdontoSpecialtySelectionProps} from './types';
import {useOdontoSpecialtySelection} from './hooks/useOdontoSpecialtySelection';
import {BookingLoadingState} from 'presentation/modules/booking/shared/ui/components/BookingLoadingState';
import {BookingErrorState} from 'presentation/modules/booking/shared/ui/components/BookingErrorState';
import {translations} from 'presentation/translations-new';
import {Stack, Typography} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {BookingEmptyState} from 'presentation/modules/booking/shared/ui/components/BookingEmptyState';
import {BookingSearchEmptyState} from 'presentation/modules/booking/shared/ui/components/BookingSearchEmptyState';
import {FlowEnum} from 'presentation/modules/booking/shared/constants/flowEnum';
import {
	AppointmentBookingEnum,
	FinishWithoutCompleteFlowEnum,
	OdontoBookingTaggingEnum,
} from 'main/analytics/gaTagsEnum';
import {OdontoSpecialtyNotFound} from './components/OdontoSpecialtyNotFound';
import {OdontoSuggestionSpecialty} from './components/OdontoSuggestionSpecialty';
import {StyledTypography} from './styles';
import {OdontoCard} from './components/OdontoCard';
import {formatText} from 'presentation/utils';
import {BookingNotFoundListCard} from 'presentation/modules/booking/shared/ui/components/BookingNotFoundListCard';
import {BookingNotFoundVariantEnum} from 'presentation/modules/booking/shared/ui/components/BookingNotFoundDialog/types';
import {PendingDialog} from 'presentation/modules/booking/shared/ui/components/PendingDialog';
import {CheckBookingModalEnum} from './hooks/useOdontoSpecialtySelection/types';
import {AlertModal} from 'presentation/components';
import {OdontoDuplicityDialog} from './components/OdontoDuplicityDialog';

const {bookOdonto} = translations['pt-br'].privateFlow.services;
const {loadingState, loadingError, notFoundModal} =
	translations['pt-br'].privateFlow.bookingFlow.specialtyFlow;
const {serverError, serverErrorTitle, tryAgain, unexpectedError, emptyDescription, resultFound} =
	translations['pt-br'].commons;

export function OdontoSpecialtySelection({handleNextStep}: OdontoSpecialtySelectionProps) {
	const {
		isFetching,
		specialtiesListError,
		specialtiesListHasUnprocessableError,
		refetch,
		query,
		showEmptyState,
		showNotFoundState,
		handleNavToStateSelection,
		specialtiesList,
		suggestionSpecialty,
		handleSelectCard,
		hideSuggestionSpecialty,
		checkBookingModal,
		loadingText,
		scheduledBooking,
		resetBookingDataSpecialties,
		setCheckBookingModal,
	} = useOdontoSpecialtySelection({handleNextStep});

	if (isFetching) {
		return (
			<BookingLoadingState
				showHeader={false}
				pageTitle={bookOdonto}
				loadingText={loadingState}
			/>
		);
	}

	if (specialtiesListError && !specialtiesListHasUnprocessableError) {
		return (
			<BookingErrorState
				showHeader={false}
				pageTitle={bookOdonto}
				errorTitle={loadingError}
				handleRefresh={refetch}
			/>
		);
	}

	if (showNotFoundState) {
		return <OdontoSpecialtyNotFound handleAction={handleNavToStateSelection} />;
	}

	if (showEmptyState) {
		return (
			<BookingSearchEmptyState
				flowType={FlowEnum.ODONTO}
				dataTagId={FinishWithoutCompleteFlowEnum.SEM_ESPECIALIDADE_ENCONTRADA}>
				<BookingEmptyState
					description={emptyDescription}
					flowType={FlowEnum.ODONTO}
					dataTagId={FinishWithoutCompleteFlowEnum.SEM_ESPECIALIDADE_ENCONTRADA}
				/>
			</BookingSearchEmptyState>
		);
	}

	return (
		<Fragment>
			{query ? (
				<Typography variant="subtitle2" color="text.secondary" mb={2}>
					{resultFound}
				</Typography>
			) : null}

			{hideSuggestionSpecialty ? null : (
				<OdontoSuggestionSpecialty
					handleSelectCard={handleSelectCard}
					suggestionSpecialty={suggestionSpecialty}
				/>
			)}

			{specialtiesList?.map(categoryItem =>
				categoryItem?.list?.length === 0 ? null : (
					<Stack key={categoryItem?.category} mt={2} gap={2}>
						<StyledTypography variant="h6" id={categoryItem?.category}>
							{categoryItem?.category}
						</StyledTypography>
						<Grid container rowSpacing={2} columnSpacing={3}>
							{categoryItem?.list.map((item, index) => (
								<Grid key={index} size={{xs: 12, lg: 6}}>
									<OdontoCard
										dataTagId={AppointmentBookingEnum.SELECIONOU_ESPECIALIDADE}
										text={formatText(item?.formattedText)}
										handleClick={() => handleSelectCard(item)}
									/>
								</Grid>
							))}
						</Grid>
					</Stack>
				),
			)}

			<Stack mt={2}>
				<BookingNotFoundListCard
					openModalDataTagId={`${OdontoBookingTaggingEnum.CLICK_NAO_ENCONTREI_ESPECIALIDADE_DESEJADA}${FlowEnum.ODONTO}`}
					modalHeaderModalDataTagId={`${OdontoBookingTaggingEnum.VISUALIZOU_MODAL_NAO_ENCONTREI_ESPECIALIDADE_DESEJADA}${FlowEnum.ODONTO}`}
					modalCloseModalDataTagId={`${OdontoBookingTaggingEnum.CLICK_FECHAR_MODAL_NAO_ENCONTREI_ESPECIALIDADE_DESEJADA}${FlowEnum.ODONTO}`}
					modalFirstActionDataTagId={`${OdontoBookingTaggingEnum.CLICK_CONSULTAR_REDE_ATENDIMENTO}${FlowEnum.ODONTO}`}
					modalSecondActionDataTagId={`${OdontoBookingTaggingEnum.CLICK_FALAR_COM_ATENDIMENTO_ONLINE}${FlowEnum.ODONTO}`}
					flowType={FlowEnum.ODONTO}
					customOrientationText={notFoundModal.cardListText}
					variant={BookingNotFoundVariantEnum.SPECIALTY}
				/>
			</Stack>

			{scheduledBooking ? (
				<OdontoDuplicityDialog
					open={checkBookingModal === CheckBookingModalEnum.DUPLICITY_STATE}
					setOpen={() => {
						resetBookingDataSpecialties();
						setCheckBookingModal('none');
					}}
					scheduledBooking={scheduledBooking}
				/>
			) : null}

			<PendingDialog
				open={
					checkBookingModal === CheckBookingModalEnum.VERIFYING_AUTH ||
					checkBookingModal === CheckBookingModalEnum.VERIFYING_DUPLICITY
				}
				text={loadingText}
			/>

			<AlertModal
				open={checkBookingModal === CheckBookingModalEnum.ERROR}
				onClose={() => {
					resetBookingDataSpecialties();
					setCheckBookingModal('none');
				}}
				description={serverError}
				title={serverErrorTitle}
				headerTitle={unexpectedError}
				buttonLabel={tryAgain}
				variant="info"
			/>
		</Fragment>
	);
}
